/** FX 機械戦略の足別アームメタ（フロント用。backend config と同趣旨） */

export const GAISUKE_FX_LEGACY_MECHANICAL_BASES = [
  'carry',
  'turtles',
  'london',
  'value',
  'legacy_rules',
] as const;

export const GAISUKE_FX_EXTRA_MECHANICAL_BASES = [
  'ma_cross',
  'rsi',
  'range_break',
  'council',
  'tokyo_fade',
  'tokyo_dir',
  'swap',
  'pause',
  'yield_mom',
  'tf_agree',
  'squeeze',
  'news',
  'crowd',
  'gotobi',
  'spike',
  'smooth',
] as const;

export const GAISUKE_FX_MECHANICAL_BASES = [
  ...GAISUKE_FX_LEGACY_MECHANICAL_BASES,
  ...GAISUKE_FX_EXTRA_MECHANICAL_BASES,
] as const;

export const GAISUKE_FX_MECHANICAL_TFS = ['1h', '4h', '1d'] as const;
export const GAISUKE_FX_POLICY_TFS = ['15m', '1h', '4h', '8h', '1d', '1wk'] as const;

export const GAISUKE_FX_TIMEFRAME_EXTRA_BASES = [
  'ma_cross',
  'rsi',
  'range_break',
  'squeeze',
  'spike',
  'smooth',
] as const;

export type GaisukeFxMechanicalBase = (typeof GAISUKE_FX_MECHANICAL_BASES)[number];
export type GaisukeFxMechanicalTf = (typeof GAISUKE_FX_POLICY_TFS)[number];

/** 既存マジックの並び。キャリー／バリューは日足のみ */
export function fxMechanicalTfsForBase(
  base: GaisukeFxMechanicalBase
): readonly GaisukeFxMechanicalTf[] {
  if (base === 'carry' || base === 'value') return ['1d'];
  if ((GAISUKE_FX_TIMEFRAME_EXTRA_BASES as readonly string[]).includes(base)) {
    return ['1h', '4h', '1d', '1wk'];
  }
  if ((GAISUKE_FX_EXTRA_MECHANICAL_BASES as readonly string[]).includes(base)) return ['1d'];
  return ['1h', '4h', '1d'];
}

/** 2026-10-17 に末尾へ足した足 */
export function fxAddedMechanicalTfsForBase(
  base: GaisukeFxMechanicalBase
): readonly GaisukeFxMechanicalTf[] {
  if (base === 'turtles' || base === 'london' || base === 'legacy_rules') {
    return ['15m', '8h', '1wk'];
  }
  if ((GAISUKE_FX_TIMEFRAME_EXTRA_BASES as readonly string[]).includes(base)) {
    return ['15m', '8h'];
  }
  return [];
}

export function fxMechanicalArmKey(
  base: GaisukeFxMechanicalBase,
  tf: GaisukeFxMechanicalTf
): string {
  return tf === '1d' ? base : `${base}_${tf}`;
}

export function fxInverseArmKey(sourceKey: string): string {
  return `${sourceKey}_inv`;
}

export function parseGaisukeFxInverseArm(key: string): string | null {
  if (!key.endsWith('_inv')) return null;
  const src = key.slice(0, -4);
  return src.length > 0 ? src : null;
}

export function parseGaisukeFxMechanicalArm(
  key: string
): { base: GaisukeFxMechanicalBase; tf: GaisukeFxMechanicalTf; inverse?: boolean } | null {
  const inv = parseGaisukeFxInverseArm(key);
  const raw = inv ?? key;
  for (const base of GAISUKE_FX_MECHANICAL_BASES) {
    if (raw === base) return { base, tf: '1d', ...(inv ? { inverse: true } : {}) };
    for (const tf of GAISUKE_FX_POLICY_TFS) {
      if (tf === '1d') continue;
      if (raw === `${base}_${tf}`) return { base, tf, ...(inv ? { inverse: true } : {}) };
    }
  }
  return null;
}

const FUSION_KEYS = ['fusion', 'fusion_1h', 'fusion_1wk'] as const;
const VENDOR_FUSION_KEYS = [
  'fusion_openai',
  'fusion_openai_1h',
  'fusion_openai_1wk',
  'fusion_anthropic',
  'fusion_anthropic_1h',
  'fusion_anthropic_1wk',
] as const;
const FUSION_4H_KEYS = ['fusion_4h', 'fusion_openai_4h', 'fusion_anthropic_4h'] as const;
const ALL_FUSION_KEYS = [...FUSION_KEYS, ...VENDOR_FUSION_KEYS, ...FUSION_4H_KEYS] as const;

const ADDED_TF_BASES = [
  'turtles',
  'london',
  'legacy_rules',
  ...GAISUKE_FX_TIMEFRAME_EXTRA_BASES,
] as const;

function addedTfArmKeys(): string[] {
  return ADDED_TF_BASES.flatMap((base) =>
    fxAddedMechanicalTfsForBase(base).map((tf) => fxMechanicalArmKey(base, tf))
  );
}

function mechanicalKeys(bases: readonly GaisukeFxMechanicalBase[]): string[] {
  const mech: string[] = [];
  for (const base of bases) {
    for (const tf of fxMechanicalTfsForBase(base)) {
      mech.push(fxMechanicalArmKey(base, tf));
    }
  }
  return mech;
}

function buildLegacyPrimaryKeys(): string[] {
  return [...FUSION_KEYS, ...mechanicalKeys(GAISUKE_FX_LEGACY_MECHANICAL_BASES)];
}

function buildExtraKeys(): string[] {
  return mechanicalKeys(GAISUKE_FX_EXTRA_MECHANICAL_BASES);
}

/** 同額タイブレーク（backend GAISUKE_FX_ARM_TIEBREAK と同じ順序） */
export const GAISUKE_FX_SCOREBOARD_TIEBREAK: Record<string, number> = {
  fusion: 0,
  fusion_1h: 1,
  fusion_1wk: 2,
  best_3d: 90,
  best_2w: 91,
  best_1w: 96,
  best_1m: 97,
  top7_3d: 86,
  top7_1w: 87,
  top7_2w: 88,
  top7_1m: 89,
  worst_3d: 92,
  worst_1w: 93,
  worst_2w: 94,
  worst_1m: 95,
  cash: 99,
};

{
  let n = 3;
  for (const base of GAISUKE_FX_LEGACY_MECHANICAL_BASES) {
    for (const tf of fxMechanicalTfsForBase(base)) {
      GAISUKE_FX_SCOREBOARD_TIEBREAK[fxMechanicalArmKey(base, tf)] = n;
      n += 1;
    }
  }
  for (const src of buildLegacyPrimaryKeys()) {
    GAISUKE_FX_SCOREBOARD_TIEBREAK[fxInverseArmKey(src)] = n;
    n += 1;
  }
  for (const key of [...buildExtraKeys(), ...buildExtraKeys().map(fxInverseArmKey)]) {
    GAISUKE_FX_SCOREBOARD_TIEBREAK[key] = n;
    n += 1;
  }
  for (const key of [...VENDOR_FUSION_KEYS, ...VENDOR_FUSION_KEYS.map(fxInverseArmKey)]) {
    GAISUKE_FX_SCOREBOARD_TIEBREAK[key] = n;
    n += 1;
  }
  const added = addedTfArmKeys();
  for (const key of [...added, ...added.map(fxInverseArmKey), ...FUSION_4H_KEYS, ...FUSION_4H_KEYS.map(fxInverseArmKey)]) {
    GAISUKE_FX_SCOREBOARD_TIEBREAK[key] = n;
    n += 1;
  }
}

export const GAISUKE_FX_BASE_COLORS: Record<string, string> = {
  fusion: '#0369a1',
  fusion_1h: '#0284c7',
  fusion_4h: '#0369a1',
  fusion_1wk: '#0ea5e9',
  fusion_openai: '#047857',
  fusion_openai_1h: '#059669',
  fusion_openai_4h: '#065f46',
  fusion_openai_1wk: '#10b981',
  fusion_anthropic: '#9a3412',
  fusion_anthropic_1h: '#c2410c',
  fusion_anthropic_4h: '#7c2d12',
  fusion_anthropic_1wk: '#ea580c',
  carry: '#b45309',
  turtles: '#7c3aed',
  london: '#c026d3',
  value: '#15803d',
  legacy_rules: '#0f766e',
  ma_cross: '#0369a1',
  rsi: '#b91c1c',
  range_break: '#9a3412',
  council: '#1d4ed8',
  tokyo_fade: '#7e22ce',
  tokyo_dir: '#a21caf',
  swap: '#b45309',
  pause: '#334155',
  yield_mom: '#c2410c',
  tf_agree: '#0f766e',
  squeeze: '#155e75',
  news: '#1e3a8a',
  crowd: '#9f1239',
  gotobi: '#a16207',
  spike: '#be123c',
  smooth: '#166534',
  best_3d: '#a16207',
  best_1w: '#0e7490',
  best_2w: '#be185d',
  best_1m: '#6d28d9',
  top7_3d: '#4f46e5',
  top7_1w: '#0891b2',
  top7_2w: '#9d174d',
  top7_1m: '#3f6212',
  worst_3d: '#7c2d12',
  worst_1w: '#9a3412',
  worst_2w: '#c2410c',
  worst_1m: '#ea580c',
  cash: '#64748b',
};

export function gaisukeFxArmColor(key: string): string {
  const inv = parseGaisukeFxInverseArm(key);
  const colorKey = inv ?? key;
  const mech = parseGaisukeFxMechanicalArm(colorKey);
  if (mech) return GAISUKE_FX_BASE_COLORS[mech.base] ?? '#0369a1';
  return GAISUKE_FX_BASE_COLORS[colorKey] ?? '#0369a1';
}

/** 会議（08:00/20:00）で方針を書くアーム＝融合系とその逆。それ以外は指標シグナル成行 */
export function isGaisukeFxConferenceArm(key: string): boolean {
  const inv = parseGaisukeFxInverseArm(key);
  const base = inv ?? key;
  return (ALL_FUSION_KEYS as readonly string[]).includes(base);
}

/**
 * MT4/MT5 の建玉コメントに書く方針略名。
 * 正本は `ea/gaisuke-fx/mt5/KensukeFxMagic.mqh` の `KensukeFxLabels`（31字で切る）。
 */
const GAISUKE_FX_EA_ORDER_COMMENTS: Record<string, string> = {
  fusion: 'Daily fusion',
  fusion_1h: '1h fusion',
  fusion_4h: '4h fusion',
  fusion_1wk: 'Weekly fusion',
  fusion_openai: 'Daily fusion (OpenAI)',
  fusion_openai_1h: '1h fusion (OpenAI)',
  fusion_openai_4h: '4h fusion (OpenAI)',
  fusion_openai_1wk: 'Weekly fusion (OpenAI)',
  fusion_anthropic: 'Daily fusion (Anthropic)',
  fusion_anthropic_1h: '1h fusion (Anthropic)',
  fusion_anthropic_4h: '4h fusion (Anthropic)',
  fusion_anthropic_1wk: 'Weekly fusion (Anthropic)',
  carry: 'Carry (Daily)',
  turtles_1h: 'Turtles (1h)',
  turtles_4h: 'Turtles (4h)',
  turtles: 'Turtles (Daily)',
  london_1h: 'London breakout (1h)',
  london_4h: 'London breakout (4h)',
  london: 'London breakout (Daily)',
  value: 'Value (Daily)',
  legacy_rules_1h: 'Technical vote (1h)',
  legacy_rules_4h: 'Technical vote (4h)',
  legacy_rules: 'Technical vote (Daily)',
  fusion_inv: 'Daily fusion · inverse',
  fusion_1h_inv: '1h fusion · inverse',
  fusion_4h_inv: '4h fusion · inverse',
  fusion_1wk_inv: 'Weekly fusion · inverse',
  fusion_openai_inv: 'Daily fusion (OpenAI) · inverse',
  fusion_openai_1h_inv: '1h fusion (OpenAI) · inverse',
  fusion_openai_4h_inv: '4h fusion (OpenAI) · inverse',
  fusion_openai_1wk_inv: 'Weekly fusion (OpenAI) · inverse',
  fusion_anthropic_inv: 'Daily fusion (Anthropic) · inverse',
  fusion_anthropic_1h_inv: '1h fusion (Anthropic) · inverse',
  fusion_anthropic_4h_inv: '4h fusion (Anthropic) · inverse',
  fusion_anthropic_1wk_inv: 'Weekly fusion (Anthropic) · inverse',
  carry_inv: 'Carry (Daily) · inverse',
  turtles_1h_inv: 'Turtles (1h) · inverse',
  turtles_4h_inv: 'Turtles (4h) · inverse',
  turtles_inv: 'Turtles (Daily) · inverse',
  london_1h_inv: 'London breakout (1h) · inverse',
  london_4h_inv: 'London breakout (4h) · inverse',
  london_inv: 'London breakout (Daily) inverse',
  value_inv: 'Value (Daily) · inverse',
  legacy_rules_1h_inv: 'Technical vote (1h) · inverse',
  legacy_rules_4h_inv: 'Technical vote (4h) · inverse',
  legacy_rules_inv: 'Technical vote (Daily) inverse',
  best_3d: '3-day leader',
  best_1w: '1-week leader',
  best_2w: '2-week leader',
  best_1m: '1-month leader',
  top7_3d: '3-day top-7 vote',
  top7_1w: '1-week top-7 vote',
  top7_2w: '2-week top-7 vote',
  top7_1m: '1-month top-7 vote',
  worst_3d: 'Contrarian 3-day',
  worst_1w: 'Contrarian 1-week',
  worst_2w: 'Contrarian 2-week',
  worst_1m: 'Contrarian 1-month',
  ma_cross: 'MA cross (Daily)',
  rsi: 'RSI reversal (Daily)',
  range_break: 'Range break (Daily)',
  council: 'Council (Daily)',
  tokyo_fade: 'Tokyo fade (Daily)',
  tokyo_dir: 'Tokyo session (Daily)',
  swap: 'Swap points (Daily)',
  pause: 'Drawdown pause (Daily)',
  yield_mom: 'Yield change (Daily)',
  tf_agree: 'Timeframe agree (Daily)',
  squeeze: 'Quiet break (Daily)',
  news: 'Headlines (Daily)',
  crowd: 'Crowd fade (Daily)',
  gotobi: 'Gotobi (Daily)',
  spike: 'Spike fade (Daily)',
  smooth: 'Smooth trend (Daily)',
  ma_cross_inv: 'MA cross (Daily) · inverse',
  rsi_inv: 'RSI reversal (Daily) · inverse',
  range_break_inv: 'Range break (Daily) · inverse',
  council_inv: 'Council (Daily) · inverse',
  tokyo_fade_inv: 'Tokyo fade (Daily) · inverse',
  tokyo_dir_inv: 'Tokyo session (Daily) · inverse',
  swap_inv: 'Swap points (Daily) · inverse',
  pause_inv: 'Drawdown pause (Daily) · inverse',
  yield_mom_inv: 'Yield change (Daily) · inverse',
  tf_agree_inv: 'Timeframe agree (Daily) · inverse',
  squeeze_inv: 'Quiet break (Daily) · inverse',
  news_inv: 'Headlines (Daily) · inverse',
  crowd_inv: 'Crowd fade (Daily) · inverse',
  gotobi_inv: 'Gotobi (Daily) · inverse',
  spike_inv: 'Spike fade (Daily) · inverse',
  smooth_inv: 'Smooth trend (Daily) · inverse',
};

const EXTRA_COMMENT_EN: Record<(typeof GAISUKE_FX_EXTRA_MECHANICAL_BASES)[number], string> = {
  ma_cross: 'MA cross',
  rsi: 'RSI reversal',
  range_break: 'Range break',
  council: 'Council',
  tokyo_fade: 'Tokyo fade',
  tokyo_dir: 'Tokyo session',
  swap: 'Swap points',
  pause: 'Drawdown pause',
  yield_mom: 'Yield change',
  tf_agree: 'Timeframe agree',
  squeeze: 'Quiet break',
  news: 'Headlines',
  crowd: 'Crowd fade',
  gotobi: 'Gotobi',
  spike: 'Spike fade',
  smooth: 'Smooth trend',
};

const LEGACY_COMMENT_EN: Record<string, string> = {
  turtles: 'Turtles',
  london: 'London breakout',
  legacy_rules: 'Technical vote',
  carry: 'Carry',
  value: 'Value',
};

const TF_COMMENT_EN: Record<GaisukeFxMechanicalTf, string> = {
  '15m': '15m',
  '1h': '1h',
  '4h': '4h',
  '8h': '8h',
  '1d': 'Daily',
  '1wk': 'Weekly',
};

function synthesizedEaComment(armKey: string): string | null {
  const inv = parseGaisukeFxInverseArm(armKey);
  const mech = parseGaisukeFxMechanicalArm(inv ?? armKey);
  if (!mech) return null;
  const name =
    EXTRA_COMMENT_EN[mech.base as keyof typeof EXTRA_COMMENT_EN] ?? LEGACY_COMMENT_EN[mech.base];
  if (!name) return null;
  const label = `${name} (${TF_COMMENT_EN[mech.tf]})`;
  return inv ? `${label} · inverse` : label;
}

/**
 * MT4 は半角31字を超えるコメントを切らず、欄ごと空にする。
 * 中黒は2字分になるので、逆方針は半角の inverse（入らなければ inv）にする。
 */
export function mt4SafeOrderComment(label: string): string {
  const dot = ' · ';
  if (label.includes(dot)) {
    const left = label.split(dot)[0] ?? '';
    const full = `${left} inverse`;
    if (full.length <= 31) return full;
    const short = `${left} inv`;
    if (short.length <= 31) return short;
  }
  return label.length > 31 ? label.slice(0, 31) : label;
}

/** EA の建玉コメントと同じ略名。EA が無い方針は null。 */
export function gaisukeFxEaOrderComment(armKey: string): string | null {
  const label = GAISUKE_FX_EA_ORDER_COMMENTS[armKey] ?? synthesizedEaComment(armKey);
  if (!label) return null;
  return mt4SafeOrderComment(label);
}

/** 公開 EA 対象。先頭30の順は固定し、2026-10-05 の方針はその後ろ */
export function gaisukeFxPublicEaArmKeys(): string[] {
  const legacy = buildLegacyPrimaryKeys();
  const extra = buildExtraKeys();
  return [
    ...legacy,
    ...legacy.map(fxInverseArmKey),
    'best_3d',
    'best_2w',
    ...extra,
    ...extra.map(fxInverseArmKey),
    'worst_3d',
    'worst_1w',
    'worst_2w',
    'worst_1m',
    'best_1w',
    'best_1m',
    'top7_3d',
    'top7_1w',
    'top7_2w',
    'top7_1m',
    ...VENDOR_FUSION_KEYS,
    ...VENDOR_FUSION_KEYS.map(fxInverseArmKey),
    ...addedTfArmKeys(),
    ...addedTfArmKeys().map(fxInverseArmKey),
    ...FUSION_4H_KEYS,
    ...FUSION_4H_KEYS.map(fxInverseArmKey),
  ];
}

/**
 * 配布ファイル名に入れる通貨ペア。MetaTrader の銘柄名（USDJPY）。
 * いまの実験はドル円だけ。別ペアを足すときは、この値をペアごとに分ける。
 */
export const GAISUKE_FX_EA_PAIR = 'USDJPY';

/** ナビゲータと配布ファイルの共通名。拡張子は MT5 が ex5、MT4 が ex4。 */
export function gaisukeFxEaFileName(armKey: string): string | null {
  if (!/^[a-z0-9_]+$/.test(armKey) || armKey === 'cash') return null;
  return `KensukeFx_${GAISUKE_FX_EA_PAIR}_${armKey}`;
}

export function gaisukeFxEaCompiledHref(
  armKey: string,
  platform: 'mt5' | 'mt4'
): string | null {
  if (!/^[a-z0-9_]+$/.test(armKey) || armKey === 'cash') return null;
  const ext = platform === 'mt5' ? 'ex5' : 'ex4';
  const name = gaisukeFxEaFileName(armKey);
  return name ? `/downloads/gaisuke-fx/${platform}/${name}.${ext}` : null;
}

export function gaisukeFxEaSourceHref(
  armKey: string,
  platform: 'mt5' | 'mt4'
): string | null {
  if (!/^[a-z0-9_]+$/.test(armKey) || armKey === 'cash') return null;
  const ext = platform === 'mt5' ? 'mq5' : 'mq4';
  const name = gaisukeFxEaFileName(armKey);
  return name ? `/downloads/gaisuke-fx/${platform}/${name}.${ext}` : null;
}

/** 方針ラッパーの `#include "KensukeFxSignalCore.mqh"` と同じファイル名 */
export function gaisukeFxEaCoreHref(platform: 'mt5' | 'mt4'): string {
  return `/downloads/gaisuke-fx/${platform}/KensukeFxSignalCore.mqh`;
}

export function gaisukeFxEaMagicHref(): string {
  return '/downloads/gaisuke-fx/KensukeFxMagic.mqh';
}

/** カードに出しているコンパイル済み EA を MT5・MT4 まとめて。 */
export function gaisukeFxEaBundleHref(): string {
  return '/downloads/gaisuke-fx/bundle/KensukeFx-USDJPY-EA.zip';
}

/** 画面からの入手は件数API経由。静的URLを直接開いた分は数えない。 */
export function gaisukeFxEaCountedDownloadHref(publicHref: string): string {
  const prefix = '/downloads/gaisuke-fx/';
  if (!publicHref.startsWith(prefix)) return publicHref;
  const file = publicHref.slice(prefix.length);
  return `/api/gaisuke/fx/ea/download?file=${encodeURIComponent(file)}`;
}
